/** @jsx tsx */
import renderer, { tsx } from "@dojo/framework/core/vdom";
import { Registry } from "@dojo/framework/core/Registry";
import { ActionCreatorsMapObject, Store } from "redux";
import { initApp } from "./CanvasAppApi";
import { presetGeometries } from "./presetShapesMeta/all";
import { eventHandler } from "./store/EventHandler";
import PageHelper from "./store/PageHelper";
import { PresetShapeInjector } from "./store/PresetShapeInjector";
import { IC8Functions, setC8Data, setC8Functions } from "./store/reducers/util";
import { ReduxInjector } from "./store/ReduxInjector";
import SelectionHelper from "./store/SelectionHelper";
import CanvasEditor from "./widgets/CanvasEditor";
import { ApiContext, IApiSet } from "./widgets/injectors/ApiContext";
import { ICanvasListeners } from "./widgets/interfaces/CanvasListeners";
import { IBrowserType } from "./widgets/interfaces/model/index";
import { IInitParams } from "./widgets/interfaces/model/InitParams";
import "snapsvg-cjs";

const DEFAULT_PARAMS = {
	dontDownloadImages: false,
	editorParentContainer: undefined,
	editorContainer: undefined,
	emitImagesLoadedEvent: false,
	initialState: {},
};

export class CanvasApp {
	public pageHelper = PageHelper;
	public selection = SelectionHelper;
	public api: {
        canvas: ActionCreatorsMapObject;
        editorParentContainer: HTMLElement;
        clipboard: ActionCreatorsMapObject;
        page: ActionCreatorsMapObject;
        selection: ActionCreatorsMapObject;
        undoRedo: {
            clear(): void;
            redo(): void;
            undo(): void;
        };
    };

	private canvasStore: Store<{}>;
	private root: HTMLElement;

	constructor(params: IInitParams = DEFAULT_PARAMS) {
		// Create a renderer to convert the virtual DOM produced by the application
		// into the rendered page.
		const r = renderer(() => <CanvasEditor />);
		this.root = params.editorContainer;

		// create redux registry
		const registry = new Registry();

		// Moved to separate method, so it can be re-used for integration testing.
		const app = initApp(params.initialState, params.isPrimaryInstance);
		this.canvasStore = app.canvasStore;
		// assigning the api object with default values
		this.api = {
			canvas: null,
			editorParentContainer: null,
			clipboard: null,
			page: null,
			selection: null,
			undoRedo: {
				clear: () => { return; },
				redo: () => { return; },
				undo: () => { return; },
			},
		};
		this.api.editorParentContainer = params.editorParentContainer;
		this.api.page = app.page;
		this.api.selection = app.selection;
		this.api.canvas = app.canvas;
		this.api.clipboard = app.clipboard;
		this.api.undoRedo = app.undoRedo;

		// we don't want to expose guidesAPI to outside world so it's not part of API.
		const guideApi = app.guides;

		// Redux action API set
		const api: IApiSet = {
			canvasContainerEl: this.api.editorParentContainer,
			canvasApi: this.api.canvas,
			clipboardApi: this.api.clipboard,
			guideApi,
			pageApi: this.api.page,
			selectionApi: this.api.selection,
		};

		const browserType: IBrowserType = params.browserType as IBrowserType;

		// Define injector canvasEditor-state that is redux store and action API set
		registry.defineInjector("canvasEditor-state", (invalidator) => {
			const injector = new ReduxInjector(this.canvasStore, new ApiContext(
				api,
				browserType,
				params.dontDownloadImages,
				params.emitImagesLoadedEvent,
			));
			injector.setInvalidator(invalidator);
			return () => injector;
		});

		// Define injector preset-shapes that is preset geometries for shapes
		registry.defineInjector("preset-shapes", (invalidator) => {
			const injector = new PresetShapeInjector(presetGeometries);
			injector.setInvalidator(invalidator);
			return () => injector;
		});

		r.mount({ registry, domNode: this.root, merge: false });
	}
	public pageApi = () => this.api.page;
	public selectionApi = () => this.api.selection;
	public canvasApi = () => this.api.canvas;
	public clipboardApi = () => this.api.clipboard;

	/**
     * Set canvas event listeners
     * @param listeners {ICanvasListeners}
     */
	public setCanvasListeners(listeners: ICanvasListeners) {
		return eventHandler.setListeners(listeners);
	}

	/**
     * Remove canvas listeners for given token
     * @param token {string}
     */
	public removeCanvasListeners(token: string) {
		eventHandler.removeListeners(token);
	}

	// hack to get svgContent
	public svgContent = () => this.root.children
        && this.root.children.length === 2
        && this.root.children[1].firstChild;

	/**
     * Set data in the datastore.
     * @param path { string } path of data in datastore (foo.bar.baz)
     * @param data { any } data to store in the datastore at the path
     */
	public setC8Data = (path: string, data: any) => {
		// call the c8-canvas function to set the data
		setC8Data(path, data);
	};

	/**
     * Set c8 functions to be accessed from canvas app.
     * @param functions { IC8Functions } object of keys and functions
     */
	public setC8Functions = (functions: IC8Functions) => {
		// call the c8-canvas function to set the c8 functions
		setC8Functions(functions);
	};
};
